import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { CreateUpload } from "@coworker/apprestructured/src/task-management/types/Upload";
import { updateImage } from "../services/images.service";
import { useAddImageToMedia } from "./useAddImageToMedia";
import { useDeleteImages } from "./useDeleteImages";
import { QueryKeys } from "./queryKeys";
import { hasFileSequenceChanged } from "../tools";
import { existingFileToImage, uploadToImage } from "../Helpers/imageHelpers";

interface UseSyncMediaImagesParams {
  mediaId?: string;
  storedImages: NewOrExistingFile[];
  currentImages: NewOrExistingFile[];
  imagesCreateUploads: CreateUpload[];
}

export const useSyncMediaImages = ({
  mediaId,
  storedImages,
  currentImages,
  imagesCreateUploads,
}: UseSyncMediaImagesParams) => {
  const queryClient = useQueryClient();
  const addImageToMedia = useAddImageToMedia();
  const deleteImageMutation = useDeleteImages();

  const [error, setError] = useState<boolean>(false);

  const syncMediaImages = useCallback(async () => {
    if (!mediaId) {
      setError(true);
      return;
    }

    const idsToDelete = storedImages
      .filter((img) => img.type === "existing")
      .filter(
        (img) =>
          !currentImages.find(
            (image) =>
              image.type === "existing" && image.file.id === img.file.id
          )
      )
      .map((img) => img.file.id!);

    if (idsToDelete.length > 0) {
      try {
        await deleteImageMutation.mutateAsync({
          imageIds: idsToDelete,
          mediaId,
        });
      } catch {
        setError(true);
      }
    }

    const hasSequenceChanged = hasFileSequenceChanged(
      storedImages,
      currentImages
    );
    if (hasSequenceChanged) {
      let shouldBreak = false;
      for (const [index, image] of currentImages.entries()) {
        if (shouldBreak) break;
        switch (image.type) {
          case "new": {
            const upload = imagesCreateUploads.find(
              (existingUpload) =>
                existingUpload.name.split(".")[0] ===
                image.file.name.split(".")[0]
            );
            if (upload) {
              try {
                await addImageToMedia.mutateAsync(
                  uploadToImage(upload, mediaId, index, image.rotation)
                );
              } catch {
                setError(true);
                shouldBreak = true;
              }
            }
            break;
          }
          case "existing": {
            try {
              await updateImage(
                image.file.id,
                existingFileToImage(image, mediaId, index)
              );
            } catch {
              setError(true);
              shouldBreak = true;
            }
            break;
          }
          default:
            break;
        }
      }
    }

    if (idsToDelete.length > 0 || hasSequenceChanged) {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.ImagesByMedia],
      });
    }
  }, [
    mediaId,
    storedImages,
    currentImages,
    deleteImageMutation,
    imagesCreateUploads,
    addImageToMedia,
    queryClient,
  ]);

  return { syncMediaImages, error };
};
